import { useState, useEffect, useMemo } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, Tag, Package, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });

export const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: ''
  });

  // "Products in category" dialog
  const [productsFor, setProductsFor] = useState(null);
  const [search, setSearch] = useState('');
  const [toAdd, setToAdd] = useState([]);

  // Delete dialog
  const [deleting, setDeleting] = useState(null);
  const [deleteMode, setDeleteMode] = useState('keep');
  const [busy, setBusy] = useState(false);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/categories`, authHeaders());
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/admin/products`, authHeaders());
      setProducts(response.data || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const refresh = () => Promise.all([fetchCategories(), fetchProducts()]);

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, []);

  const handleAdd = () => {
    setFormData({ name: '', slug: '' });
    setEditMode(false);
    setDialog(true);
  };

  const handleEdit = (category) => {
    setSelectedCategory(category);
    setFormData({
      name: category.name,
      slug: category.slug
    });
    setEditMode(true);
    setDialog(true);
  };

  const handleSubmit = async () => {
    try {
      if (editMode) {
        await axios.put(`${API}/admin/categories/${selectedCategory.category_id}`, formData, authHeaders());
        toast.success('Category updated successfully');
      } else {
        await axios.post(`${API}/admin/categories`, formData, authHeaders());
        toast.success('Category created successfully');
      }

      setDialog(false);
      refresh();
    } catch (error) {
      console.error('Failed to save category:', error);
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  // ---------- delete ----------
  const openDelete = (category) => {
    setDeleting(category);
    setDeleteMode('keep');
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      const query = deleting.product_count > 0 ? `?products=${deleteMode}` : '';
      const { data } = await axios.delete(`${API}/admin/categories/${deleting.category_id}${query}`, authHeaders());
      if (data.products_deleted) toast.success(`Category and ${data.products_deleted} product(s) deleted`);
      else if (data.products_uncategorized) toast.success(`Category deleted, ${data.products_uncategorized} product(s) kept without a category`);
      else toast.success('Category deleted successfully');
      setDeleting(null);
      refresh();
    } catch (error) {
      console.error('Failed to delete category:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    } finally {
      setBusy(false);
    }
  };

  // ---------- products in a category ----------
  const openProducts = (category) => {
    setProductsFor(category);
    setSearch('');
    setToAdd([]);
    fetchProducts();
  };

  const inCategory = useMemo(
    () => (productsFor ? products.filter((p) => p.category_id === productsFor.category_id) : []),
    [products, productsFor]
  );

  const candidates = useMemo(() => {
    if (!productsFor) return [];
    const q = search.trim().toLowerCase();
    return products
      .filter((p) => p.category_id !== productsFor.category_id)
      .filter((p) => !q || (p.name || '').toLowerCase().includes(q));
  }, [products, productsFor, search]);

  const toggleAdd = (id) => setToAdd((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const addSelected = async () => {
    if (!toAdd.length) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/categories/${productsFor.category_id}/products`,
        { product_ids: toAdd },
        authHeaders()
      );
      toast.success(`${data.updated} product(s) added to ${productsFor.name}`);
      setToAdd([]);
      refresh();
    } catch (error) {
      console.error('Failed to add products:', error);
      toast.error(error.response?.data?.detail || 'Failed to add products');
    } finally {
      setBusy(false);
    }
  };

  const removeFromCategory = async (product) => {
    try {
      await axios.delete(`${API}/admin/categories/${productsFor.category_id}/products/${product.product_id}`, authHeaders());
      toast.success(`${product.name} removed from ${productsFor.name}`);
      refresh();
    } catch (error) {
      console.error('Failed to remove product:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove product');
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  return (
    <AdminLayout>
      <div className="p-6 md:p-8" data-testid="admin-categories">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground mb-2">Categories</h1>
            <p className="text-muted-foreground">Organize your products into categories</p>
          </div>
          <Button
            onClick={handleAdd}
            className="bg-accent text-accent-foreground hover:bg-accent/90"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Category
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground">Loading categories...</p>
          </div>
        ) : categories.length === 0 ? (
          <div className="text-center py-20">
            <Tag className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No categories yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((category) => (
              <div
                key={category.category_id}
                data-testid={`category-${category.slug}`}
                className="p-6 rounded-2xl bg-card border border-border hover:border-accent/50 transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-foreground mb-1">{category.name}</h3>
                    <p className="text-sm text-muted-foreground">/{category.slug}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(category)}
                      aria-label={`Edit ${category.name}`}
                      className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => openDelete(category)}
                      aria-label={`Delete ${category.name}`}
                      data-testid={`delete-category-${category.slug}`}
                      className="p-2 rounded-lg hover:bg-red-500/10 transition-colors text-muted-foreground hover:text-red-500"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="px-3 py-1 rounded-full bg-accent/10 text-accent font-medium">
                    {category.product_count} product{category.product_count !== 1 ? 's' : ''}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openProducts(category)}
                    data-testid={`manage-products-${category.slug}`}
                  >
                    <Package className="w-4 h-4 mr-2" />
                    Products
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialog} onOpenChange={setDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="text-2xl font-heading font-bold text-foreground">
                {editMode ? 'Edit Category' : 'Add Category'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label htmlFor="name">Category Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setFormData({
                      name,
                      slug: generateSlug(name)
                    });
                  }}
                  className="mt-2 bg-input border-border text-foreground"
                  placeholder="e.g., Vibrators"
                />
              </div>
              <div>
                <Label htmlFor="slug">Slug (URL)</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  className="mt-2 bg-input border-border text-foreground font-mono"
                  placeholder="e.g., vibrators"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Used in URLs. Use lowercase letters, numbers, and hyphens only.
                </p>
              </div>
              <div className="flex gap-4 pt-4">
                <Button
                  onClick={handleSubmit}
                  className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90"
                >
                  {editMode ? 'Save Changes' : 'Create Category'}
                </Button>
                <Button onClick={() => setDialog(false)} variant="outline" className="flex-1">
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Products in a category */}
        <Dialog open={!!productsFor} onOpenChange={(open) => !open && setProductsFor(null)}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="category-products-dialog">
            <DialogHeader>
              <DialogTitle className="text-2xl font-heading font-bold text-foreground">
                {productsFor?.name}: products
              </DialogTitle>
              <DialogDescription>
                A product belongs to one category. Removing it keeps it in the shop without a category.
              </DialogDescription>
            </DialogHeader>

            <section className="mt-2">
              <h3 className="font-semibold text-foreground mb-2">In this category ({inCategory.length})</h3>
              {inCategory.length === 0 ? (
                <p className="text-sm text-muted-foreground">No products yet — add some below.</p>
              ) : (
                <ul className="divide-y divide-border rounded-xl border border-border">
                  {inCategory.map((p) => (
                    <li key={p.product_id} className="flex items-center gap-3 p-3">
                      <img src={p.image_url} alt="" className="h-10 w-10 flex-none rounded-lg object-cover" />
                      <span className="flex-1 text-sm text-foreground line-clamp-1">{p.name}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeFromCategory(p)}
                        data-testid={`remove-from-category-${p.product_id}`}
                      >
                        <X className="w-4 h-4 mr-1" />
                        Remove
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="mt-6">
              <h3 className="font-semibold text-foreground mb-2">Add products</h3>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search products…"
                aria-label="Search products"
                className="bg-input border-border text-foreground"
              />
              <ul className="mt-2 max-h-64 overflow-y-auto divide-y divide-border rounded-xl border border-border">
                {candidates.length === 0 ? (
                  <li className="p-3 text-sm text-muted-foreground">No other products</li>
                ) : (
                  candidates.map((p) => (
                    <li key={p.product_id}>
                      <label className="flex cursor-pointer items-center gap-3 p-3 hover:bg-muted/50">
                        <input
                          type="checkbox"
                          checked={toAdd.includes(p.product_id)}
                          onChange={() => toggleAdd(p.product_id)}
                          data-testid={`add-to-category-${p.product_id}`}
                          className="h-4 w-4"
                        />
                        <img src={p.image_url} alt="" className="h-10 w-10 flex-none rounded-lg object-cover" />
                        <span className="flex-1 text-sm text-foreground line-clamp-1">{p.name}</span>
                        <span className="text-xs text-muted-foreground">{p.category_name}</span>
                      </label>
                    </li>
                  ))
                )}
              </ul>
              <Button
                onClick={addSelected}
                disabled={!toAdd.length || busy}
                className="mt-3 w-full bg-accent text-accent-foreground hover:bg-accent/90"
                data-testid="add-selected-btn"
              >
                Add {toAdd.length || ''} selected to {productsFor?.name}
              </Button>
              <p className="mt-1 text-xs text-muted-foreground">Products that are in another category will be moved here.</p>
            </section>
          </DialogContent>
        </Dialog>

        {/* Delete category */}
        <Dialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
          <DialogContent className="bg-card border-border" data-testid="delete-category-dialog">
            <DialogHeader>
              <DialogTitle className="text-2xl font-heading font-bold text-foreground">
                Delete “{deleting?.name}”?
              </DialogTitle>
              <DialogDescription>
                {deleting?.product_count
                  ? `This category has ${deleting.product_count} product(s). What should happen to them?`
                  : 'This category is empty.'}
              </DialogDescription>
            </DialogHeader>

            {deleting?.product_count > 0 && (
              <fieldset className="mt-2 space-y-2">
                <legend className="sr-only">Products in this category</legend>
                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border p-3">
                  <input
                    type="radio"
                    name="delete-mode"
                    value="keep"
                    checked={deleteMode === 'keep'}
                    onChange={() => setDeleteMode('keep')}
                    className="mt-1"
                    data-testid="delete-mode-keep"
                  />
                  <span>
                    <span className="block font-medium text-foreground">Keep the products</span>
                    <span className="block text-sm text-muted-foreground">They stay in the shop without a category.</span>
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-red-500/40 p-3">
                  <input
                    type="radio"
                    name="delete-mode"
                    value="delete"
                    checked={deleteMode === 'delete'}
                    onChange={() => setDeleteMode('delete')}
                    className="mt-1"
                    data-testid="delete-mode-delete"
                  />
                  <span>
                    <span className="block font-medium text-red-500">Delete the {deleting.product_count} product(s) too</span>
                    <span className="block text-sm text-muted-foreground">This can't be undone.</span>
                  </span>
                </label>
              </fieldset>
            )}

            <div className="flex gap-4 pt-4">
              <Button
                onClick={confirmDelete}
                disabled={busy}
                className="flex-1 bg-red-500 text-white hover:bg-red-600"
                data-testid="confirm-delete-category"
              >
                {deleting?.product_count > 0 && deleteMode === 'delete' ? 'Delete category and products' : 'Delete category'}
              </Button>
              <Button onClick={() => setDeleting(null)} variant="outline" className="flex-1">
                Cancel
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};
